import { settingsRoutes } from "@/app";
import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { Link } from "@/components/flexkit/Link";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { cn } from "@/components/podkit/lib/cn";
import { nonSettingsPageNavigationEntryPredicate } from "@/components/settings/settings";
import { SettingsMenu } from "@/components/settings/SettingsMenu";
import { ResizableSidebar } from "@/components/SidebarLayout";
import { Tooltip } from "@/components/Tooltip";

import { useLastNavigationEntry } from "@/hooks/use-last-navigation-entry";
import { TrackLocations } from "@/hooks/use-segment";
import type { MenuItem } from "@/menu-item";
import { populateMenuFromRoute } from "@/menu-item";
import { useMemo, type FC } from "react";

export const SettingsSidebar: FC<{ className?: string; minWidth?: number; maxWidth?: number }> = ({
    className,
    minWidth,
    maxWidth,
}) => {
    const { isMobileViewport } = useIsMobileViewport();

    return (
        <ResizableSidebar
            className={className}
            minWidth={minWidth}
            maxWidth={maxWidth}
            handleClassName="top-4 mr-[1px] h-[calc(100%-32px)]"
            showHandle={!isMobileViewport}
        >
            <div data-testid="sidebar" className="flex size-full flex-col pb-[6px] pt-0.5">
                <div className="px-4 pt-2">
                    <SidebarHeader />
                </div>
                {isMobileViewport && (
                    <div className="mx-2 rounded-lg border border-border-subtle">
                        <OrgSwitcher
                            data-track-location={TrackLocations.SettingsSidebarOrgSwitcher}
                            isLocked
                            hideKeyboardShortcuts
                        />
                    </div>
                )}
                <ScrollArea
                    orientation="vertical"
                    className={cn("mr-0.5 flex-grow pr-[1px]", isMobileViewport ? "pt-1" : "pt-2")}
                >
                    <SidebarContent />
                </ScrollArea>
                {!isMobileViewport && <SettingsSidebarFooter />}
            </div>
        </ResizableSidebar>
    );
};

const SidebarHeader: FC = () => {
    const { isMobileViewport } = useIsMobileViewport();
    const lastVisitedNonSettingsPage = useLastNavigationEntry(nonSettingsPageNavigationEntryPredicate);

    return (
        <Tooltip
            content={
                <div className="flex flex-row items-center gap-2">
                    Back to Ona
                    {!isMobileViewport && <KeyboardCombo keys={["Esc"]} />}
                </div>
            }
        >
            <Link
                className="flex h-8 flex-row items-center gap-1 rounded-lg hover:bg-surface-03"
                to={lastVisitedNonSettingsPage}
            >
                <div className="w-5">
                    <IconArrowLeft size="base" />
                </div>

                <div className={cn("flex-grow truncate text-start text-base font-bold")}>Back to Ona</div>
            </Link>
        </Tooltip>
    );
};

const SidebarContent: FC = () => {
    const menu: MenuItem[] = useMemo(() => populateMenuFromRoute(settingsRoutes), []);
    return <SettingsMenu menu={menu} />;
};

const SettingsSidebarFooter: FC = () => {
    return (
        <div className="flex flex-col gap-2 p-2 pb-2 pt-4">
            <div className="rounded-lg">
                <OrgSwitcher
                    data-track-location={TrackLocations.SettingsSidebarOrgSwitcher}
                    hideKeyboardShortcuts
                    isLocked
                />
            </div>
        </div>
    );
};
